import React, { useEffect, useState } from "react";
import axios from "axios";

const NewCustomersPage = ({ startDate, endDate }) => {
  const [collections, setCollections] = useState([]);
  const [filteredCollections, setFilteredCollections] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const baseURL = process.env.REACT_APP_BASE_URL;

  const formatDate = (dateStr) => {
    const [day, month, year] = dateStr.split("-");
    return `${year}-${month}-${day}`;
  };

  const formatDateAndDay = (dateString) => {
    const dateObj = new Date(dateString);

    const formattedDate = dateObj
      .toLocaleDateString("en-GB")
      .replace(/\//g, "-");

    const dayOfWeek = dateObj.toLocaleDateString("en-US", { weekday: "long" });

    return { formattedDate, dayOfWeek };
  };

  // Fetch all data on initial load
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const accessToken = sessionStorage.getItem("access_token");
        const response = await axios.get(`${baseURL}/getAllLoan`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        setCollections(response.data);
        setFilteredCollections(response.data); // Initially show all data
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCollections();
  }, []);

  // Filter data based on the date range
  useEffect(() => {
    const start = new Date(formatDate(startDate));
    const end = new Date(formatDate(endDate));

    const filtered = collections.filter((collection) => {
      const collectionDate = new Date(collection.start_date);
      return collectionDate >= start && collectionDate <= end;
    });

    setFilteredCollections(filtered);

    // Calculate grand total
    const total = filtered.reduce(
      (acc, item) => acc + parseFloat(item.amount || 0),
      0
    );
    setGrandTotal(total);
  }, [startDate, endDate, collections]);

  // Group collections by agent
  const groupByAgent = (data) => {
    return data.reduce((acc, item) => {
      const agent = item.collectionagent_name || "Unknown Agent";
      if (!acc[agent]) {
        acc[agent] = [];
      }
      acc[agent].push(item);
      return acc;
    }, {});
  };

  const groupedData = groupByAgent(filteredCollections);
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-lg font-semibold text-gray-600">Loading…</p>
      </div>
    );
  }

  return (
    <div className="p-5 bg-gray-50">
      <h2 className="text-lg font-bold mb-5 text-center">Agent Customers</h2>
      <h3 className="text-lg font-bold mb-3 text-center">
        {startDate} - {endDate}
      </h3>

      <h3 className="text-center mb-4">
        Total Customers: {filteredCollections.length} | Grand Total Amount: ₹
        {grandTotal.toFixed(2)}
      </h3>
      {Object.keys(groupedData).length > 0 ? (
        Object.keys(groupedData).map((agent, index) => {
          const agentData = groupedData[agent];

          // Calculate total amount for the agent
          const totalAmount = agentData.reduce(
            (acc, item) => acc + parseFloat(item.amount || 0),
            0
          );

          return (
            <div key={index} className="mb-8">
              {/* Agent Name */}
              <h4 className="text-xl font-semibold mb-4">{agent}</h4>

              <div className="overflow-x-auto">
                <table className="table-auto w-full border-collapse border border-gray-300 bg-white shadow-md rounded-lg">
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 w-20 text-left">
                        S.No
                      </th>
                      <th className="border border-gray-300 px-4 py-2 w-30 text-left">
                        Customer
                      </th>
                      <th className="border border-gray-300 px-4 py-2 w-30 text-left">
                        Date
                      </th>
                      <th className="border border-gray-300 px-4 py-2 w-30 text-left">
                        Day
                      </th>
                      <th className="border border-gray-300 px-4 py-2 w-30 text-left">
                        Scheme
                      </th>
                      <th className="border border-gray-300 px-4 py-2 w-30 text-left">
                        Loan Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentData.map((item, idx) => {
                      const { formattedDate, dayOfWeek } = formatDateAndDay(
                        item.start_date
                      );

                      // Apply alternating row colors
                      const rowClass =
                        idx % 2 === 0 ? "bg-white" : "bg-gray-100";

                      return (
                        <tr
                          key={item.id}
                          className={`${rowClass} border-b border-gray-200`}
                        >
                          <td className="border border-gray-300 px-4 py-2 w-20">
                            {idx + 1}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 w-30">
                            {item.tamilname || "N/A"}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 w-30">
                            {formattedDate || "N/A"}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 w-30">
                            {dayOfWeek || "N/A"}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 w-30">
                            {item.scheme_name || "N/A"}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 w-30">
                            ₹{parseFloat(item.amount || 0).toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Total Amount Row for each agent */}
                    <tr className="bg-gray-200 font-bold">
                      <td colSpan="5" className="py-2 px-4 text-right">
                        Total:
                      </td>
                      <td className="py-2 px-4">₹{totalAmount.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      ) : (
        <div className="text-center py-4 text-gray-500">
          No data available for the selected date range.
        </div>
      )}
    </div>
  );
};

export default NewCustomersPage;
